import React from "react";
import { v4 as uuidv4 } from "uuid";
import useExcelUpload, {
  generateRandomAmount,
  generateRandomDescription,
} from "../../../../utils/hooks/useExcelUpload";
import UploadExcelDialog from "../../utils/UploadExcelEnhanced";
import {
  ASSET_EXPENSED_OFF_CATEGORY,
  createEmptyAsset,
  deriveAsset,
  EXTERNAL_TYPES,
  getClaimYearsFromSetting,
  getFaRegisterCaTypes,
  isAssetExpensedOffCategory,
  isEnhancedTwdvType,
} from "./rules";

const getInstructions = (currentYa, caSettings, caTypeOptions) => {
  const enhancedTypes = getFaRegisterCaTypes(caSettings).filter((type) =>
    isEnhancedTwdvType(type, caSettings)
  );
  const enhancedText = enhancedTypes.length
    ? enhancedTypes.join(", ")
    : "enhanced allowance CA types";

  return [
    ["Col", "Comments"],
    [
      "A",
      `Asset category. Use "${ASSET_EXPENSED_OFF_CATEGORY}" for assets expensed in P&L schedules. If the category does not exist, it will be created automatically.`,
    ],
    [
      "B",
      "Asset description. Describe the asset in a few words. This will be used to identify the asset in the FA register.",
    ],
    ["C", "Cost of asset. Whole dollar amount."],
    [
      "D",
      `YA of purchase. Must not be later than current YA. For "${ASSET_EXPENSED_OFF_CATEGORY}", it must be earlier than current YA.`,
    ],
    [
      "E",
      "Ensure that the CA Type is based on the following options: " +
        caTypeOptions.join(", "),
    ],
    [
      "F",
      "Optional. Number of years of claim. For fixed-year CA types, the system will replace this with the statutory fixed value. Ignored for Hire Purchase, Renovation, Revenue and No Claim.",
    ],
    [
      "G",
      "Optional. Number of years brought forward. Required for prior YA assets with an applicable CA Type; current YA assets are auto-derived.",
    ],
    [
      "H",
      "Optional. True if the asset is deferred in the current year. Ignored for Hire Purchase, Renovation, Revenue and No Claim.",
    ],
    [
      "I",
      "Optional. True if the asset is disposed in the current year. Ignored for Hire Purchase, Renovation, Revenue and No Claim.",
    ],
    [
      "J",
      "Optional. Base TWDV brought forward. Required for prior YA assets with an applicable CA Type; current YA assets are auto-derived.",
    ],
    [
      "K",
      `Enhanced TWDV brought forward. Only applies to ${enhancedText}. Leave blank for other CA types.`,
    ],
  ];
};

const toBoolean = (value) => {
  if (value === true || value === false) return value;
  const normalized = String(value ?? "")
    .trim()
    .toLowerCase();
  if (["true", "yes", "1"].includes(normalized)) return true;
  if (["false", "no", "0", ""].includes(normalized)) return false;
  return Boolean(value);
};

const toWholeNumber = (value, fallback = "") => {
  if (value === "" || value === null || value === undefined) return fallback;
  const number = Number(value);
  return Number.isFinite(number) ? Math.round(number) : fallback;
};

const getClaimYears = (caType, uploadedValue, caSettings) => {
  const presetClaimYears = getClaimYearsFromSetting(caType, caSettings);
  if (presetClaimYears !== null) return presetClaimYears;
  return toWholeNumber(uploadedValue);
};

const getExpensedOffCurrentYaIssues = (rows = [], currentYa) =>
  (Array.isArray(rows) ? rows : [])
    .map((row, index) => {
      const caType = row[4] || "";
      const isExternalType = EXTERNAL_TYPES.has(caType);
      const categoryName = String(row[0] || "").trim();
      const isExpensedOffAsset =
        !isExternalType && isAssetExpensedOffCategory(categoryName);
      const uploadedYaOfPurchase = toWholeNumber(row[3]);

      if (
        !isExpensedOffAsset ||
        Number(uploadedYaOfPurchase) < Number(currentYa)
      ) {
        return null;
      }

      const description = String(row[1] || "").trim();

      return {
        rowNumber: index + 2,
        description: description || `Asset on row ${index + 2}`,
        yaOfPurchase: uploadedYaOfPurchase,
      };
    })
    .filter(Boolean);

const buildAssetRow = (row, currentYa, caSettings, categoryOptions) => {
  const rowKey = uuidv4();
  const caType = row[4] || "";
  const isExternalType = EXTERNAL_TYPES.has(caType);
  const categoryName = String(row[0] || "").trim();
  const isExpensedOffAsset =
    !isExternalType && isAssetExpensedOffCategory(categoryName);
  const uploadedYaOfPurchase = toWholeNumber(row[3]);
  const yaOfPurchase =
    isExpensedOffAsset && Number(uploadedYaOfPurchase) >= Number(currentYa)
      ? ""
      : uploadedYaOfPurchase;
  const selectedCategory = categoryOptions.find(
    (category) =>
      String(category.label || "")
        .trim()
        .toLowerCase() === categoryName.toLowerCase()
  );

  return deriveAsset(
    {
      ...createEmptyAsset(rowKey),
      assetCategoryId: isExpensedOffAsset
        ? ASSET_EXPENSED_OFF_CATEGORY
        : selectedCategory?.value || "",
      assetCategory: isExpensedOffAsset
        ? ASSET_EXPENSED_OFF_CATEGORY
        : selectedCategory?.label || categoryName,
      assetDescription: row[1] || "",
      costOfAsset: toWholeNumber(row[2]),
      yaOfPurchase,
      caType,
      isPLScheduleAsset: isExpensedOffAsset,
      noOfYearsClaim: getClaimYears(caType, row[5], caSettings),
      noOfYearsBf: toWholeNumber(row[6]),
      deferred: isExternalType ? null : toBoolean(row[7]),
      disposal: isExternalType ? false : toBoolean(row[8]),
      twdvBf: toWholeNumber(row[9]),
      enhancedTwdvBf: isEnhancedTwdvType(caType, caSettings)
        ? toWholeNumber(row[10])
        : null,
    },
    currentYa,
    caSettings
  );
};

const FaRegisterExcelUpload = ({
  currentYa,
  caSettings,
  categoryOptions = [],
  onExcelAddRows,
  open,
  handleCloseDialog,
}) => {
  const caTypes = getFaRegisterCaTypes(caSettings);
  const enhancedCaType = caTypes.find((type) =>
    isEnhancedTwdvType(type, caSettings)
  );
  const applicableCaTypes = caTypes.filter((type) => !EXTERNAL_TYPES.has(type));
  const sampleCaType = applicableCaTypes[0] || caTypes[0] || "";
  const sampleCategory =
    categoryOptions.find(
      (category) => !isAssetExpensedOffCategory(category.label)
    )?.label ||
    categoryOptions[0]?.label ||
    "";
  const secondSampleCaType =
    applicableCaTypes.find((type) => type !== sampleCaType) || sampleCaType;
  const sampleExternalType =
    caTypes.find((type) => type === "Hire Purchase") ||
    caTypes.find((type) => EXTERNAL_TYPES.has(type));

  const {
    selectedFile,
    uploading,
    alertMessage,
    alertSeverity,
    errorFileUrl,
    rowCount,
    handleCloseAlert,
    handleDownloadTemplate,
    handleFileChange,
    handleResetFile,
    handleUpload,
    getTemplateHeadersString,
  } = useExcelUpload({
    fileName: "fa_register_upload_template.xlsx",
    instructions: getInstructions(currentYa, caSettings, caTypes),
    sampleData: [
      {
        "Asset Category": sampleCategory,
        "Asset Description": generateRandomDescription(),
        "Cost of Asset": generateRandomAmount(),
        "YA of Purchase": currentYa,
        "CA Type": sampleCaType,
        "No. of Years of Claim": 3,
        "No. of Years B/F": "",
        "Deferred in current year?": "false",
        "Disposed in current year?": "false",
        "Base TWDV B/F": "",
        "Enhanced TWDV B/F": "",
      },
      {
        "Asset Category": sampleCategory,
        "Asset Description": generateRandomDescription(),
        "Cost of Asset": 3000,
        "YA of Purchase": Number(currentYa) - 1,
        "CA Type": secondSampleCaType,
        "No. of Years of Claim": 3,
        "No. of Years B/F": 2,
        "Deferred in current year?": "false",
        "Disposed in current year?": "false",
        "Base TWDV B/F": 2000,
        "Enhanced TWDV B/F": "",
      },
      ...(sampleExternalType
        ? [
            {
              "Asset Category": sampleCategory,
              "Asset Description": "Furniture and fittings",
              "Cost of Asset": 45000,
              "YA of Purchase": currentYa,
              "CA Type": sampleExternalType,
              "No. of Years of Claim": "",
              "No. of Years B/F": "",
              "Deferred in current year?": "",
              "Disposed in current year?": "",
              "Base TWDV B/F": "",
              "Enhanced TWDV B/F": "",
            },
          ]
        : []),
      {
        "Asset Category": sampleCategory,
        "Asset Description": generateRandomDescription(),
        "Cost of Asset": generateRandomAmount(),
        "YA of Purchase": currentYa,
        "CA Type": enhancedCaType || caTypes[0] || "",
        "No. of Years of Claim": enhancedCaType
          ? (getClaimYearsFromSetting(enhancedCaType, caSettings) ?? 3)
          : "",
        "No. of Years B/F": "",
        "Deferred in current year?": "false",
        "Disposed in current year?": "false",
        "Base TWDV B/F": "",
        "Enhanced TWDV B/F": enhancedCaType ? 0 : "",
      },
    ],
    validationSchema: [
      {
        type: "text",
        required: true,
      },
      { type: "text" },
      { type: "number", round: true, required: true },
      { type: "number", round: true, required: true },
      { type: "text", required: true, fixedValue: caTypes },
      { type: "number", round: true },
      { type: "number", round: true },
      { type: "boolean" },
      { type: "boolean" },
      { type: "number", round: true },
      { type: "number", round: true },
    ],
    onUpload: (excelData, uploadedRowCount) => {
      if (uploadedRowCount > 0) {
        const expensedOffCurrentYaIssues = getExpensedOffCurrentYaIssues(
          excelData,
          currentYa
        );

        onExcelAddRows(
          excelData.map((row) =>
            buildAssetRow(row, currentYa, caSettings, categoryOptions)
          ),
          { expensedOffCurrentYaIssues }
        );
      }
    },
  });

  const fixedValueGroups = [
    {
      label: "Asset Category",
      values: categoryOptions.map((category) => category.label),
    },
    {
      label: "CA Type",
      values: caTypes,
    },
  ];

  return (
    <UploadExcelDialog
      open={open}
      selectedFile={selectedFile}
      uploading={uploading}
      alertMessage={alertMessage}
      alertSeverity={alertSeverity}
      errorFileUrl={errorFileUrl}
      validRows={rowCount}
      onClose={handleCloseDialog}
      onDownloadTemplate={handleDownloadTemplate}
      onFileChange={handleFileChange}
      onUpload={handleUpload}
      onCloseAlert={handleCloseAlert}
      onReset={handleResetFile}
      templateHeadersString={getTemplateHeadersString()}
      templateInstructions={getInstructions(currentYa, caSettings, caTypes)}
      fixedValueGroups={fixedValueGroups}
    />
  );
};

export default FaRegisterExcelUpload;
